import { LogOut, ShieldAlert } from 'lucide-react';

import { Button } from '../core/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../core/Dialog';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';
import { LOGIN_PATH } from '../../constants/routes';

/**
 * Blocks the app once the API has rejected our token.
 *
 * The session is already destroyed by the time this renders — this exists so the user
 * is told why the page stopped working instead of silently losing whatever they were
 * doing, and so the page behind it cannot be interacted with in the meantime.
 */
export function SessionExpiredDialog() {
  const { sessionExpired } = useAuth();
  const { t } = useTranslation();

  if (!sessionExpired) return null;

  return (
    <Dialog open>
      <DialogContent
        hideClose
        className="max-w-md"
        // No escape hatch: dismissing would leave the user on a dashboard that can no
        // longer reach the API, which is the confusing state this whole dialog exists
        // to replace.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
              <ShieldAlert className="h-5 w-5 text-amber-400" />
            </div>
            <DialogTitle>{t('session.title')}</DialogTitle>
          </div>
          <DialogDescription className="pt-3 text-slate-400">
            {t('session.description')}
          </DialogDescription>
        </DialogHeader>

        <Button
          className="mt-6 w-full"
          // A full navigation rather than a router push: it discards every piece of state
          // still held from the dead session instead of trusting each screen to reset.
          onClick={() => window.location.replace(LOGIN_PATH)}
        >
          <LogOut className="h-4 w-4" />
          {t('session.logout')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
